// HomeScreen.js
import React, { useCallback, useEffect, useState } from "react";
import CustomWeb from "../../components/atoms/Webview";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import {
  Alert,
  DeviceEventEmitter,
  Linking,
  Platform,
  View,
} from "react-native";
import * as Device from "expo-device";
import { DEVICE_SIZE, SYSTEM_PLATFORM } from "@utils/index";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import i18next from "i18next";
import * as Updates from "expo-updates";
import Routes from "@navigations/Routes";
import { navigate, navigationRef } from "@navigations/utils/RootNavigation";
import { useFocusEffect, useLinkTo } from "@react-navigation/native";
import { ProductCategory } from "@utils/constants";
import { useDispatch } from "react-redux";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useSelector } from "react-redux";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useTheme } from "styled-components/native";

import { navigateToRoute } from "@scenes/home/utils";
import { EventType } from "@utils/analytics";
import { useAdBatchTracking } from "../../hooks/useAdBatchTracking";
import JBTdropdown from "@components/molecules/JBTdropdown";
import { useTranslation } from "react-i18next";
import { useProductPrefix } from "../../hooks/useProductPrefix";
import { replaceDeeplinkScheme } from "@utils/product/productValidators";
import { useAppSettings } from "@redux/slices/globalSlice";
import { useCameraPermissions } from "expo-camera";
import { usePrivateAppGuard } from "@utils/usePrivateAppGuard";
import { appendTrackingQueryParams } from "@utils/appendTrackingQueryParams";
import { initializeHomeHeight } from "../../iframe/height/homeHeight";

type TrackInfo = {
  [key: string]: string | number | null | undefined;
};

const HomeScreen = ({ route }: any) => {
  const useUser = useUserInfo();
  const { settings, isJbtDisabled } = useOrgSettingsSlice();
  const [iframeUrl, setIframeUrl] = useState("");
  const getAppSettings = useAppSettings();
  const { shouldBlock: shouldBlockPublicAccess } =
    usePrivateAppGuard("go-back");
  const productPrefix = useProductPrefix();
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const theme = useTheme();
  const dispatch = useDispatch();
  const { trackBatchedAdEvent } = useAdBatchTracking();
  const [modalType, setModalType] = useState<"jbt" | null>(null);
  const [webKey, setWebKey] = useState(0);
  const { t } = useTranslation();
  const landingPageUrls = getAppSettings?.landing_page_urls ?? "";
  const linkTo = useLinkTo();
  const [permission, requestPermission] = useCameraPermissions();
  const handleBannerAds = async (data: any) => {
    trackBatchedAdEvent(EventType.VIEW_BANNER, data.payload.properties);
  };

  const isJSON = (str: any) => {
    try {
      JSON.parse(str);
      return true;
    } catch (error) {
      return false;
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== "web" || !useUser.isIFrame()) return;
      return initializeHomeHeight();
    }, [useUser])
  );

  useEffect(() => {
    if (Platform.OS === "web") {
      const handleMessage = (event: any) => {
        handleOnMessage(event.data);
      };

      window.addEventListener("message", handleMessage);

      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, []);

  const requestCameraPermission = async () => {
    if (permission?.canAskAgain) {
      const result = await requestPermission();
      if (result.granted) {
        return;
      }
    }

    Alert.alert(
      t("barcodeScanner.permission.alert.title.text", {
        defaultValue: "Camera Permission Required",
      }),
      t("barcodeScanner.permission.alert.message.text", {
        defaultValue:
          "Camera access has been denied. Please enable it in your device settings to use the barcode scanner.",
      }),
      [
        {
          text: t("barcodeScanner.permission.alert.cancel.text", {
            defaultValue: "Cancel",
          }),
          style: "cancel",
        },
        {
          text: t("barcodeScanner.permission.alert.openSettings.text", {
            defaultValue: "Open Settings",
          }),
          onPress: () => Linking.openSettings(),
        },
      ]
    );
  };

  const handleBarcodePress = async () => {
    if (!permission?.granted) {
      if (permission?.canAskAgain) {
        const mResult = await requestPermission();
        if (!mResult.granted) {
          return;
        }
      } else {
        requestCameraPermission();
        return;
      }
    }

    navigate(Routes.GlobalNavigator, {
      screen: Routes.BarcodeScannerScreen,
    });
  };

  const handleOnMessage = (e: any) => {
    try {
      const data = JSON.parse(e);
      const payload = data.payload;

      if (data?.type === "trackEventInSegment") {
        handleBannerAds(data);
      }

      switch (data.action) {
        case "redirection":
          redirectBasedOnActionData(payload);
          break;
        case "deeplink":
          let deeplinkUrl = payload;
          deeplinkUrl = replaceDeeplinkScheme(
            deeplinkUrl,
            process.env.EXPO_PUBLIC_APP_SCHEME || ""
          );

          deeplinkUrl += deeplinkUrl.includes("?")
            ? "&fromHomePage=true"
            : "?fromHomePage=true";

          if (deeplinkUrl.includes("/item-detail/")) {
            DeviceEventEmitter.emit("CLEAR_JEWELRY_CONTEXT");
          }

          if (Platform.OS !== "web") {
            Linking.openURL(deeplinkUrl);
          } else if (
            Platform.OS === "web" &&
            deeplinkUrl.startsWith("/webapp/")
          ) {
            linkTo(deeplinkUrl);
          }
          break;
        case "external":
          Linking.openURL(payload);
          break;
        case "scanner":
          handleBarcodePress();
          break;
        default:
          break;
      }
    } catch (error) {
      //TODO handle error
    }
  };

  const redirectBasedOnActionData = ({
    action_data,
    navigator,
    screen,
    params,
  }: any) => {
    switch (action_data) {
      case ProductCategory.DIAMOND:
        navigateToRoute(
          ProductCategory.DIAMOND,
          dispatch,
          theme,
          SearchFilterTypeEnum.SINGLE_STONES,
          productPrefix
        );
        break;
      case ProductCategory.JEWELRY:
        navigateToRoute(
          ProductCategory.JEWELRY,
          dispatch,
          theme,
          SearchFilterTypeEnum.JEWELRY,
          productPrefix
        );
        break;
      case ProductCategory.GEMSTONE:
        navigateToRoute(
          ProductCategory.GEMSTONE,
          dispatch,
          theme,
          SearchFilterTypeEnum.SINGLE_STONES,
          productPrefix
        );
        break;
      case ProductCategory.LAB_GROWN_DIAMOND:
        navigateToRoute(
          ProductCategory.LAB_GROWN_DIAMOND,
          dispatch,
          theme,
          SearchFilterTypeEnum.SINGLE_STONES,
          productPrefix
        );
        break;
      case ProductCategory.LABGROWN_JEWELRY:
        navigateToRoute(
          ProductCategory.JEWELRY,
          dispatch,
          theme,
          SearchFilterTypeEnum.LAB_GROWN_JEWELRY,
          productPrefix
        );
        break;
      case "jbt_connect":
        if (!isJbtDisabled) {
          // @ts-ignore
          theme.isLargeScreen
            ? setModalType("jbt")
            : navigate(Routes.MoreNavigator);
        }
        break;
      case "text-search":
        // @ts-ignore
        navigate(Routes.GlobalNavigator, {
          initial: true,
          screen: Routes.SearchText,
        });
        break;
      case "navigation":
        navigate(navigator, {
          screen:
            screen === Routes.FilterSplitScreen
              ? theme.isMobile
                ? Routes.SearchResultScreen
                : Routes.FilterSplitScreen
              : screen,
          params,
          initial: false,
        });
        break;
      default:
        if (action_data && action_data.startsWith("http")) {
          Linking.openURL(action_data);
        }
        break;
    }
  };

  useEffect(() => {
    const generateTrackParams = () => {
      let urlParams = "";

      const track_info: TrackInfo = {
        userid: useUser.user.id?.toString(),
        token: useUser.getToken(),
        device: Platform.OS,
        device_type: Platform.OS,
        domain: process.env.EXPO_PUBLIC_API_URL || "",
        system_browser: Platform.Version,
        os_version: Device?.osVersion,
        system_os: Device?.osName || "",
        system_screen_size: `${DEVICE_SIZE.width}x${DEVICE_SIZE.height}`,
        system_minimal_view: process.env.EXPO_PUBLIC_SYSTEM_MINIMAL_VIEW,
        segment_key: settings.init_status.segment_write_key,
        org_id: process.env.EXPO_PUBLIC_ORGANIZATION_ID,
        system_platform: SYSTEM_PLATFORM?.toString(),
        system_app_version: Updates.runtimeVersion,
        app_version: Updates.runtimeVersion,
        installation_identifier: installationIdentifier,
        locale: i18next.language || "en",
        cy: useUser?.user?.currency || "USD",
        analytics_approach: "2",
        user_price_mode: useUser.user.price_mode?.toString(),
        vdb_version: "2.0",
      };

      Object.keys(track_info).forEach((k) => {
        urlParams += `&${k}=${track_info?.[k]}`;
      });
      if (Platform.OS !== "web") {
        urlParams += `&hbmenu=false`;
      }
      let mUrl = "";
      const firstSearchParams = `first_search=${useUser.user.first_search}${urlParams}`;
      if (route?.params?.iframeUrl) {
        mUrl = appendTrackingQueryParams(
          route.params.iframeUrl,
          firstSearchParams
        );
      } else if (
        useUser.isIFrame() &&
        landingPageUrls?.internal_landing_page_iframe_url
      ) {
        mUrl = appendTrackingQueryParams(
          landingPageUrls.internal_landing_page_iframe_url,
          firstSearchParams
        );
      } else if (useUser.user.custom_homepage_url_v3) {
        const baseUrl = useUser.user.custom_homepage_url_v3?.replace(
          "?scroll=true",
          ""
        );
        mUrl = appendTrackingQueryParams(baseUrl, firstSearchParams);
      } else if (
        settings.organization.settings.enable_external_landing_page &&
        settings.organization.external_landing_page_url_v3
      ) {
        mUrl = appendTrackingQueryParams(
          settings.organization.external_landing_page_url_v3,
          urlParams
        );
      } else if (
        settings.organization.settings.enable_external_landing_page &&
        landingPageUrls?.external_landing_page_url
      ) {
        mUrl = appendTrackingQueryParams(
          landingPageUrls.external_landing_page_url,
          firstSearchParams
        );
      } else if (
        settings.organization.settings.enable_external_landing_page &&
        settings.organization.external_landing_page_url
      ) {
        mUrl = appendTrackingQueryParams(
          settings.organization.external_landing_page_url,
          firstSearchParams
        );
      }
      setIframeUrl(mUrl);
    };
    generateTrackParams();
  }, [useUser, settings]);

  useEffect(() => {
    if (route?.params?.resetToDefault === true) {
      setWebKey((k) => k + 1);
      const params = route?.params ?? {};
      delete params.resetToDefault;
      delete params.iframeUrl;
      (navigationRef as any).setParams(params ?? {});
    }
  }, [route?.params?.resetToDefault]);

  if (shouldBlockPublicAccess) {
    return <View />;
  }

  return (
    <LayoutContainer>
      <CustomWeb
        key={webKey}
        url={iframeUrl}
        showExtraBottomView={true}
        onMessage={(event: any) => {
          const rawData = event?.nativeEvent?.data;
          const data = isJSON(rawData) ? JSON.parse(rawData) : rawData;
          if (data.type === "trackEventInSegment") {
            handleBannerAds(data);
          } else {
            handleOnMessage(event?.nativeEvent?.data);
          }
        }}
      />
      {!isJbtDisabled && (
        <JBTdropdown
          isOpen={modalType === "jbt"}
          onClose={() => setModalType(null)}
          HtmlSource={{
            html: t("jbt.screenText.footer.text"),
          }}
          styleKey={"Minor-R"}
        />
      )}
    </LayoutContainer>
  );
};

export default HomeScreen;
